import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorBanner from '../components/ErrorBanner';
import { resolveMediaUrl } from '../utils/mediaUrl';

/**
 * Home — buyer-first (feel safe, easy to shop). Same safety message also shows
 * market owners why this wins buyers away from Facebook Marketplace.
 * Single market = one large centered card (no side whitespace).
 */
export default function Marketplace() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [markets, setMarkets] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api('/api/markets', {});
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Error ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) setMarkets(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <LoadingSpinner message="Loading markets…" />;
  }

  const single = markets.length === 1;

  return (
    <div className="flex flex-col -mx-4 md:-mx-8 -mt-4 min-h-[70vh] bg-[#f0f4f8] dark:bg-slate-950">
      <section className="px-4 md:px-8 pt-8 pb-6">
        <div className="max-w-xl mx-auto text-center flex flex-col gap-3">
          <p className="text-xs font-bold tracking-[0.14em] uppercase text-emerald-700 dark:text-emerald-400">
            Flea Market Hub
          </p>
          <h1 className="text-3xl md:text-[2.5rem] font-bold text-slate-900 dark:text-slate-50 leading-tight tracking-tight">
            Shop real booths.
            <br />
            Pay safe.
          </h1>
          <p className="text-base text-slate-600 dark:text-slate-300 leading-snug max-w-md mx-auto">
            Verified vendors. In-app checkout. Pickup at a real booth.
          </p>
          <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 pt-1">
            <li>Fast</li>
            <li aria-hidden className="text-slate-300 dark:text-slate-600">·</li>
            <li>Convenient</li>
            <li aria-hidden className="text-slate-300 dark:text-slate-600">·</li>
            <li>Safe</li>
          </ul>
        </div>
      </section>

      {error && (
        <div className="px-4 md:px-8">
          <ErrorBanner message={error} />
        </div>
      )}

      <section className="px-4 md:px-8 pb-12 flex-1">
        <div className={`mx-auto ${single ? 'max-w-xl' : 'max-w-2xl'}`}>
          {markets.length === 0 ? (
            <p className="text-center text-slate-600 dark:text-slate-400 py-10">
              No markets open right now.
            </p>
          ) : (
            <>
              {!single && (
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 text-center mb-4">
                  Choose a market
                </h2>
              )}
              <ul
                className={
                  single
                    ? 'flex justify-center'
                    : 'grid grid-cols-1 sm:grid-cols-2 gap-4'
                }
              >
                {markets.map((market) => {
                  const img = market.imageUrl ? resolveMediaUrl(market.imageUrl) : null;
                  return (
                    <li key={market.id} className={single ? 'w-full' : undefined}>
                      <button
                        type="button"
                        onClick={() => navigate(`/market/${market.id}`)}
                        className={`w-full text-center bg-white dark:bg-slate-800 overflow-hidden border border-slate-200/80 dark:border-slate-700 hover:border-emerald-500 dark:hover:border-emerald-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                          single ? 'rounded-2xl shadow-md' : 'rounded-xl'
                        }`}
                      >
                        <div
                          className={`w-full bg-slate-200 dark:bg-slate-700 overflow-hidden ${
                            single ? 'aspect-[16/10]' : 'aspect-[16/10]'
                          }`}
                        >
                          {img ? (
                            <img
                              src={img}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <img
                                src="/icons8-online-store-100.png"
                                alt=""
                                className="w-20 h-20 object-contain opacity-70"
                              />
                            </div>
                          )}
                        </div>
                        <div className={`flex flex-col items-center ${single ? 'p-5 gap-2' : 'p-4 gap-1'}`}>
                          <h3
                            className={`font-bold text-slate-900 dark:text-slate-50 line-clamp-2 ${
                              single ? 'text-2xl' : 'text-lg'
                            }`}
                          >
                            {market.name}
                          </h3>
                          {market.address && (
                            <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                              {market.address}
                            </p>
                          )}
                          {(market.daysOpen || market.operatingHours) && (
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {[market.daysOpen, market.operatingHours].filter(Boolean).join(' · ')}
                            </p>
                          )}
                          <span
                            className={`inline-flex items-center justify-center mt-2 rounded-xl font-semibold text-white bg-emerald-600 hover:bg-emerald-700 ${
                              single ? 'h-12 text-base px-6' : 'h-10 text-sm px-4'
                            }`}
                          >
                            {single ? 'Shop this market' : 'Shop →'}
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
